import axios from 'axios';
import { ESPLoader, Transport } from 'esptool-js';
import { ESP_USB_VENDOR_IDS } from '../constants/deviceConfig';

const COMPILE_TIMEOUT_MS = 180000;
const CONNECT_ATTEMPTS = 3;

const DEFAULT_SERIAL_OPTIONS = {
  baudRate: 115200,
  dataBits: 8,
  stopBits: 1,
  parity: 'none',
  flowControl: 'none',
};

const BOOTLOADER_HINT = 'Hold BOOT (IO0), tap EN/RESET once, then release BOOT when connecting.';

function buildPortFilters() {
  return ESP_USB_VENDOR_IDS.map((usbVendorId) => ({ usbVendorId }));
}

async function requestEspPort() {
  if (!navigator.serial) {
    throw new Error('Web Serial not supported – use Chrome or Edge.');
  }

  try {
    return await navigator.serial.requestPort({ filters: buildPortFilters() });
  } catch (err) {
    if (err.name === 'NotFoundError') {
      return navigator.serial.requestPort();
    }
    throw err;
  }
}

async function acquireEspPort(onProgress) {
  const existingPorts = await navigator.serial.getPorts();
  if (existingPorts.length === 1) {
    onProgress?.('Using previously authorized USB port.');
    return existingPorts[0];
  }

  onProgress?.('Select your ESP32 USB port in the browser dialog…');
  return requestEspPort();
}

function base64ToBinaryString(base64) {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  let str = '';
  const chunkSize = 16384;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    str += String.fromCharCode.apply(null, chunk);
  }
  return str;
}

function createLoaderTerminal(onProgress) {
  if (!onProgress) return undefined;
  return {
    clean() {},
    writeLine(data) {
      onProgress(data);
    },
    write(data) {
      if (typeof data === 'string' && data.trim()) {
        onProgress(data.trim());
      }
    },
  };
}

async function releasePort(port) {
  if (!port) return;
  if (port.readable?.locked) {
    const reader = port.readable.getReader();
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
  if (port.writable?.locked) {
    const writer = port.writable.getWriter();
    await writer.close().catch(() => {});
    writer.releaseLock();
  }
  if (port.readable || port.writable) {
    await port.close().catch(() => {});
  }
}

async function connectWithRetries(port, baudrate, onProgress) {
  let lastError = null;

  for (let attempt = 1; attempt <= CONNECT_ATTEMPTS; attempt += 1) {
    const transport = new Transport(port, true);
    const esploader = new ESPLoader({
      transport,
      baudrate,
      romBaudrate: 115200,
      debugLogging: process.env.NODE_ENV !== 'production',
      terminal: createLoaderTerminal(onProgress),
      serialOptions: DEFAULT_SERIAL_OPTIONS,
    });

    try {
      if (attempt === 1) {
        onProgress?.('Connecting to bootloader (USB auto-reset)…');
      } else {
        onProgress?.(
          `Retry ${attempt}/${CONNECT_ATTEMPTS}: hold BOOT, tap EN/RESET, release BOOT now…`,
        );
        await new Promise((resolve) => setTimeout(resolve, 800));
      }

      await esploader.main();
      return { transport, esploader };
    } catch (err) {
      lastError = err;
      await transport.disconnect().catch(() => {});
      if (attempt < CONNECT_ATTEMPTS) {
        onProgress?.(`Connect attempt ${attempt} failed: ${err.message}`);
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
    }
  }

  const detail = lastError?.message || 'Unknown error';
  throw new Error(`Failed to connect after ${CONNECT_ATTEMPTS} attempts (${detail}). ${BOOTLOADER_HINT}`);
}

function buildFlashFileArray(segments) {
  return segments.map((segment) => ({
    address: segment.address,
    data: base64ToBinaryString(segment.data),
  }));
}

async function compileProgramPayload({
  deviceId,
  sketch,
  board,
  flashMode,
  flashFreq,
  flashSize,
  token,
}) {
  const { data } = await axios.post(
    `${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}/program`,
    { sketchCode: sketch, board, flashMode, flashFreq, flashSize },
    {
      headers: { Authorization: `Bearer ${token}` },
      timeout: COMPILE_TIMEOUT_MS,
    },
  );
  return data;
}

export async function flashFirmware({
  deviceId,
  sketch,
  board,
  flashMode,
  flashFreq,
  flashSize,
  baudrate,
  token,
  onProgress,
}) {
  let port = null;

  try {
    onProgress?.('Compiling on server — approve the USB port dialog if it appears…');

    const [programPayload, selectedPort] = await Promise.all([
      compileProgramPayload({
        deviceId,
        sketch,
        board,
        flashMode,
        flashFreq,
        flashSize,
        token,
      }),
      acquireEspPort(onProgress),
    ]);

    port = selectedPort;

    const {
      segments,
      flashMode: imageFlashMode,
      flashFreq: imageFlashFreq,
      flashSize: imageFlashSize,
    } = programPayload;

    if (!Array.isArray(segments) || segments.length === 0) {
      throw new Error('Server returned no flash segments.');
    }

    const totalBytes = segments.reduce((sum, segment) => sum + atob(segment.data).length, 0);
    const fileArray = buildFlashFileArray(segments);

    onProgress?.(
      `Firmware ready (${Math.round(totalBytes / 1024)} KB, ${segments.length} segments, `
      + `${imageFlashMode}/${imageFlashFreq}/${imageFlashSize}). Connecting immediately…`,
    );

    const { transport, esploader } = await connectWithRetries(port, baudrate, onProgress);

    onProgress?.('Connected! Erasing and flashing…');

    try {
      await esploader.writeFlash({
        fileArray,
        eraseAll: true,
        compress: true,
        flashMode: 'keep',
        flashFreq: 'keep',
        flashSize: 'keep',
        reportProgress: (fileIndex, written, total) => {
          if (total > 0) {
            const segmentLabel = `segment ${fileIndex + 1}/${fileArray.length}`;
            onProgress?.(`Flashing ${segmentLabel}... ${Math.round((written / total) * 100)}%`);
          }
        },
      });

      await esploader.after();

      // Pulse EN/RESET via RTS — after() alone is unreliable on some USB-UART chips.
      await transport.setRTS(true);
      await new Promise((resolve) => setTimeout(resolve, 100));
      await transport.setRTS(false);
      await new Promise((resolve) => setTimeout(resolve, 300));
    } catch (err) {
      const detail = err?.message || String(err);
      throw new Error(`Flash failed: ${detail}`);
    } finally {
      await transport.disconnect().catch(() => {});
    }

    return 'Done! Device reset — WiFi/MQTT may take 20–30s. Tap EN/RESET if the dashboard stays offline.';
  } finally {
    await releasePort(port);
  }
}

export function formatFlashError(err) {
  let msg = err.message || 'Unknown error';

  if (err.code === 'ECONNABORTED') {
    return Promise.resolve('Compile timed out on server — try again or check server logs.');
  }

  if (err.response?.data) {
    const body = err.response.data;
    if (typeof body === 'object' && body !== null) {
      if (Array.isArray(body.errors)) {
        return Promise.resolve(body.errors.map((e) => e.msg).join('; '));
      }
      return Promise.resolve(body.error || JSON.stringify(body));
    }
    if (body instanceof Blob) {
      return body.text().then((text) => {
        try {
          const json = JSON.parse(text);
          return json.error || text;
        } catch {
          return text;
        }
      });
    }
  }

  if (err.name === 'NotAllowedError') msg = 'Port access denied.';
  if (err.name === 'NotFoundError') msg = 'No port selected.';
  if (err.message?.includes('already open')) msg = 'Port locked — close Serial Monitor, refresh the tab, and try again.';

  return Promise.resolve(msg);
}